import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import TelefoneInput from './TelefoneInput';
import PageLayout from './PageLayout';
import { theme, t } from '../theme';
import { norm, formatFatiasKg, overlayCloseProps } from '../utils';
import { imprimirRecibo } from '../platform/arquivos';
import { isNative } from '../platform/native';
import {
  FaPlus, FaTimes, FaSearch, FaCalendarAlt, FaList, FaChevronLeft, FaChevronRight,
  FaWhatsapp, FaEdit, FaTrash, FaEye, FaCheck, FaPrint, FaCommentDots, FaCamera, FaDollarSign,
  FaExchangeAlt, FaPhone, FaMapMarkerAlt, FaClock, FaUser, FaArrowRight, FaShareAlt
} from 'react-icons/fa';
import { useConfirm } from './ConfirmProvider';
import useIsMounted from '../hooks/useIsMounted';

const CLOUDINARY_CLOUD = process.env.REACT_APP_CLOUDINARY_CLOUD || '';
const CLOUDINARY_PRESET = process.env.REACT_APP_CLOUDINARY_PRESET || 'metria_unsigned';

async function comprimirImagem(file, maxWidth = 1200, quality = 0.7) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let w = img.width, h = img.height;
        if (w > maxWidth) { h = (maxWidth / w) * h; w = maxWidth; }
        canvas.width = w; canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        canvas.toBlob((blob) => resolve(blob), 'image/jpeg', quality);
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}

async function uploadCloudinary(file, folder = 'pedidos') {
  if (!CLOUDINARY_CLOUD) {
    console.warn('CLOUDINARY_CLOUD não configurado');
    return null;
  }
  const compressed = await comprimirImagem(file);
  const formData = new FormData();
  formData.append('file', compressed, 'foto.jpg');
  formData.append('upload_preset', CLOUDINARY_PRESET);
  formData.append('folder', folder);
  const res = await fetch(`https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD}/image/upload`, { method: 'POST', body: formData });
  const data = await res.json();
  return data.secure_url || null;
}

const STATUS_FLOW = ['confirmado', 'em_producao', 'pronto', 'entregue'];
const STATUS_META = {
  confirmado:  { label: 'Confirmado',  next: 'em_producao', cor: '#27ae60', proximoLabel: 'Em produção' },
  em_producao: { label: 'Em produção', next: 'pronto',      cor: '#f39c12', proximoLabel: 'Pronto' },
  pronto:      { label: 'Pronto',      next: 'entregue',    cor: '#3498db', proximoLabel: 'Retirado' },
  entregue:    { label: 'Retirado',    next: null,          cor: '#7f8c8d' },
  cancelado:   { label: 'Cancelado',   next: null,          cor: '#e74c3c' },
};
const STATUS_LABELS = Object.fromEntries(Object.entries(STATUS_META).map(([k, v]) => [k, v.label]));
const STATUS_CORES = Object.fromEntries(Object.entries(STATUS_META).map(([k, v]) => [k, v.cor]));
// Defensive compat for any leftover 'pendente' references
STATUS_LABELS.pendente = 'Confirmado';
STATUS_CORES.pendente = '#27ae60';
const STATUS_FORM = STATUS_FLOW;
const METODOS = ['dinheiro', 'pix', 'cartao_credito', 'cartao_debito', 'boleto', 'transferencia'];
const METODO_LABEL = {
  dinheiro: 'Dinheiro', pix: 'Pix', cartao_credito: 'Cartão Crédito',
  cartao_debito: 'Cartão Débito', boleto: 'Boleto', transferencia: 'Transferência',
};

function formatarTelefone(tel) {
  if (!tel) return '';
  const d = String(tel).replace(/\D/g, '');
  // Remove DDI 55 inicial se for 12 ou 13 dígitos
  const num = (d.length === 12 || d.length === 13) && d.startsWith('55') ? d.slice(2) : d;
  if (num.length === 11) return `(${num.slice(0, 2)}) ${num.slice(2, 7)}-${num.slice(7)}`;
  if (num.length === 10) return `(${num.slice(0, 2)}) ${num.slice(2, 6)}-${num.slice(6)}`;
  if (num.length === 9) return `${num.slice(0, 5)}-${num.slice(5)}`;
  if (num.length === 8) return `${num.slice(0, 4)}-${num.slice(4)}`;
  return tel;
}

function formatarMoedaInput(valor) {
  const num = valor.replace(/\D/g, '');
  if (!num) return '';
  return (parseInt(num, 10) / 100).toFixed(2).replace('.', ',');
}

function parseMoeda(valor) {
  if (!valor) return 0;
  return parseFloat(valor.replace(/\./g, '').replace(',', '.')) || 0;
}

const Pedidos = () => {
  const [view, setView] = useState('lista');
  const [aba, setAba] = useState('ativos'); // 'ativos' | 'arquivados'
  const [pedidos, setPedidos] = useState([]);
  const [pedidosArquivados, setPedidosArquivados] = useState([]);
  const [arquivosPendentes, setArquivosPendentes] = useState([]);
  const [menuStatusPara, setMenuStatusPara] = useState(null);
  const [calendario, setCalendario] = useState({});
  const [mesAtual, setMesAtual] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`; });
  const [busca, setBusca] = useState('');
  const [diaSelecionado, setDiaSelecionado] = useState(null);
  const [pedidosDia, setPedidosDia] = useState([]);
  const [loading, setLoading] = useState(true);

  // Dados auxiliares
  const [clientes, setClientes] = useState([]);
  const [tamanhos, setTamanhos] = useState([]);
  const [recheios, setRecheios] = useState([]);
  const [fichasMassa, setFichasMassa] = useState([]);
  const [coberturas, setCoberturas] = useState([]);
  const [menuFixo, setMenuFixo] = useState([]);

  // Modais
  const [showForm, setShowForm] = useState(false);
  const [editandoPedido, setEditandoPedido] = useState(null);
  const [detalhePedido, setDetalhePedido] = useState(null);
  const [pagamentoPara, setPagamentoPara] = useState(null);
  const confirm = useConfirm();
  const mounted = useIsMounted();

  useEffect(() => { carregarDados(); }, []);
  // Carrega pedidos no mount imediatamente (independente dos filtros)
  useEffect(() => { carregarPedidos(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  // Abre modal automaticamente quando navega via ?novo=1 (espera carregar).
  // NÃO removemos a query string — qualquer setSearchParams causa re-render
  // que pode disparar outras useEffects e fechar o modal.
  const queroAbrirNovoRef = React.useRef(
    typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('novo') === '1'
  );
  useEffect(() => {
    if (loading) return;
    if (!queroAbrirNovoRef.current) return;
    queroAbrirNovoRef.current = false;
    setEditandoPedido(null);
    setShowForm(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);
  // Debounce só quando filtros/busca mudam
  useEffect(() => {
    const timer = setTimeout(() => carregarPedidos(), busca ? 250 : 0);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busca, diaSelecionado, aba]);
  // Recarrega quando a aba/janela volta a ficar visível
  useEffect(() => {
    const onVis = () => { if (document.visibilityState === 'visible') carregarPedidos(); };
    window.addEventListener('focus', onVis);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('focus', onVis);
      document.removeEventListener('visibilitychange', onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { if (view === 'calendario') carregarCalendario(); }, [view, mesAtual]);

  const carregarPedidosDia = async (data) => {
    try {
      const res = await api.get(`/api/pedidos?data=${data}`);
      if (!mounted.current) return;
      setPedidosDia(res.data?.pedidos || res.data || []);
    } catch { if (mounted.current) setPedidosDia([]); }
  };

  const carregarDados = async () => {
    try {
      const [resC, resT, resR, resF, resMenu] = await Promise.all([
        api.get('/api/clientes'),
        api.get('/guia/tamanhos'),
        api.get('/guia/recheios'),
        api.get('/fichas/api/fichas-tecnicas'),
        api.get('/guia/menu-fixo'),
      ]);
      if (!mounted.current) return;
      setClientes(resC.data);
      setTamanhos(resT.data);
      setRecheios(resR.data);
      setMenuFixo(resMenu.data || []);
      const todasFichas = resF.data?.fichas || [];
      setFichasMassa(todasFichas.filter(f => f.categoria?.toLowerCase() === 'massas de bolo'));
      setCoberturas(todasFichas.filter(f => (f.categoria || '').toLowerCase().includes('cobertura')));
    } catch {} finally { if (mounted.current) setLoading(false); }
  };

  const reqIdRef = React.useRef(0);
  const carregarPedidos = async () => {
    const myReqId = ++reqIdRef.current;
    try {
      const params = { _t: Date.now() }; // cache-buster contra resposta stale do navegador
      if (busca) params.busca = busca;
      if (diaSelecionado) params.data = diaSelecionado;
      if (aba === 'arquivados') params.arquivados = 1;
      const res = await api.get('/api/pedidos', { params });
      if (myReqId !== reqIdRef.current) return;
      if (!mounted.current) return;
      const lista = Array.isArray(res.data) ? res.data : (res.data?.pedidos || []);
      if (aba === 'arquivados') setPedidosArquivados(lista);
      else setPedidos(lista);
    } catch {}
  };

  const carregarCalendario = async () => {
    try {
      const res = await api.get(`/api/pedidos/calendario?mes=${mesAtual}`);
      if (!mounted.current) return;
      setCalendario(res.data);
    } catch {}
  };

  const mudarStatus = async (id, status) => {
    setPedidos(prev => prev.map(p => p.id === id ? { ...p, status } : p));
    try { await api.patch(`/api/pedidos/${id}/status`, { status }); }
    catch { carregarPedidos(); }
  };

  const deletarPedido = async (id) => {
    if (!await confirm('Remover pedido?', { danger: true, confirmText: 'Excluir' })) return;
    setPedidos(prev => prev.filter(p => p.id !== id));
    try { await api.delete(`/api/pedidos/${id}`); } catch { carregarPedidos(); }
  };

  const agendarArquivo = (pedidoId, statusAnterior) => {
    setArquivosPendentes(prev => [
      ...prev.filter(a => a.id !== pedidoId),
      { id: pedidoId, status_anterior: statusAnterior, deadline_ts: Date.now() + 10000 },
    ]);
  };

  const avancarStatus = async (pedido) => {
    const meta = STATUS_META[pedido.status];
    if (!meta?.next) return;
    if (meta.next === 'entregue' && (pedido.valor_restante || 0) > 0) {
      await confirm(`Pagamento pendente: falta ${formatarMoeda(pedido.valor_restante)}. Registre o pagamento antes de marcar como retirado.`,
        { confirmText: 'Entendi', danger: false });
      return;
    }
    const statusAnterior = pedido.status;
    await mudarStatus(pedido.id, meta.next);
    if (meta.next === 'entregue') agendarArquivo(pedido.id, statusAnterior);
  };

  const cancelarPedido = async (pedido) => {
    const ok = await confirm(`Cancelar pedido #${pedido.id}?`, { danger: true, confirmText: 'Cancelar pedido' });
    if (!ok) return;
    const statusAnterior = pedido.status;
    await mudarStatus(pedido.id, 'cancelado');
    agendarArquivo(pedido.id, statusAnterior);
  };

  const desfazerArquivo = async (pedidoId) => {
    const arq = arquivosPendentes.find(a => a.id === pedidoId);
    if (!arq) return;
    setArquivosPendentes(prev => prev.filter(a => a.id !== pedidoId));
    await mudarStatus(pedidoId, arq.status_anterior);
  };

  const restaurarArquivado = async (pedidoId) => {
    try {
      await api.patch(`/api/pedidos/${pedidoId}/desarquivar`);
      setPedidosArquivados(prev => prev.filter(p => p.id !== pedidoId));
      carregarPedidos();
    } catch {}
  };

  // Processa fila de arquivamento: ao expirar deadline, chama PATCH /arquivar
  useEffect(() => {
    if (arquivosPendentes.length === 0) return;
    const timers = arquivosPendentes.map(arq => {
      const ms = Math.max(0, arq.deadline_ts - Date.now());
      return setTimeout(async () => {
        let pedidoArquivado = null;
        try {
          const res = await api.patch(`/api/pedidos/${arq.id}/arquivar`);
          pedidoArquivado = res.data;
        } catch {}
        if (!mounted.current) return;
        setArquivosPendentes(prev => prev.filter(a => a.id !== arq.id));
        setPedidos(prev => prev.filter(p => p.id !== arq.id));
        // Adiciona ao cache de arquivados imediatamente (sem refresh manual)
        if (pedidoArquivado) {
          setPedidosArquivados(prev => [pedidoArquivado, ...prev.filter(p => p.id !== pedidoArquivado.id)]);
        }
      }, ms);
    });
    return () => timers.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arquivosPendentes.map(a => a.id).join(',')]);

  const abrirDetalhe = async (id) => {
    try { const res = await api.get(`/api/pedidos/${id}`); setDetalhePedido(res.data); } catch {}
  };

  const formatarMoeda = (v) => `R$ ${(v||0).toFixed(2).replace('.',',')}`;
  const formatarData = (d) => d ? new Date(d+'T00:00:00').toLocaleDateString('pt-BR') : '';
  const formatarDataRel = (dataIso) => {
    if (!dataIso) return '';
    const hoje = new Date(); hoje.setHours(0,0,0,0);
    const d = new Date(dataIso + 'T00:00:00');
    const diff = Math.round((d - hoje) / 86400000);
    if (diff === 0) return 'hoje';
    if (diff === 1) return 'amanhã';
    return d.toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'short' });
  };
  const iniciais = (nome) => (nome || '?').split(' ').filter(Boolean).slice(0, 2).map(s => s[0].toUpperCase()).join('');

  // ---------- Calendário ----------
  const renderCalendario = () => {
    const [ano, mes] = mesAtual.split('-').map(Number);
    const primeiroDia = new Date(ano, mes - 1, 1).getDay();
    const diasNoMes = new Date(ano, mes, 0).getDate();
    const hoje = new Date();
    const hojeStr = `${hoje.getFullYear()}-${String(hoje.getMonth()+1).padStart(2,'0')}-${String(hoje.getDate()).padStart(2,'0')}`;
    const meses = ['','Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];

    const cells = [];
    for (let i = 0; i < primeiroDia; i++) cells.push(<td key={`e${i}`} style={cs.calCell} />);
    for (let d = 1; d <= diasNoMes; d++) {
      const dateStr = `${ano}-${String(mes).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
      const info = calendario[dateStr];
      const isHoje = dateStr === hojeStr;
      const temPedido = info && info.total > 0;
      const isSelecionado = dateStr === diaSelecionado;
      cells.push(
        <td key={d} style={{ ...cs.calCell, background: isSelecionado ? '#F5E6E3' : temPedido ? '#fef3f0' : 'transparent', cursor: temPedido ? 'pointer' : 'default', borderBottom: isSelecionado ? `2px solid ${theme.colors.primary}` : undefined }}
          onClick={() => { if (temPedido) { setDiaSelecionado(isSelecionado ? null : dateStr); if (!isSelecionado) carregarPedidosDia(dateStr); else setPedidosDia([]); } }}>
          <div style={{ position: 'relative' }}>
            <span style={{ ...cs.calDay, background: isHoje ? '#d4807c' : 'transparent', color: isHoje ? '#fff' : '#333', borderRadius: '50%', width: 28, height: 28, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{d}</span>
            {temPedido && <span style={cs.calBadge}>{info.total}</span>}
          </div>
          {temPedido && info.nomes && (
            <div style={{ marginTop: 2 }}>
              {info.nomes.slice(0, 2).map((n, i) => (
                <div key={i} style={{ fontSize: 10, color: theme.colors.primary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.3 }}>{n}</div>
              ))}
              {info.nomes.length > 2 && <div style={{ fontSize: 9, color: theme.colors.textMuted }}>+{info.nomes.length - 2} mais</div>}
            </div>
          )}
        </td>
      );
    }
    const rows = [];
    for (let i = 0; i < cells.length; i += 7) rows.push(<tr key={i}>{cells.slice(i, i + 7)}</tr>);

    return (
      <div style={cs.calContainer}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <button onClick={() => { const m = mes === 1 ? 12 : mes-1; const a = mes === 1 ? ano-1 : ano; setMesAtual(`${a}-${String(m).padStart(2,'0')}`); }} style={cs.calNav}><FaChevronLeft /></button>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>{meses[mes]} {ano}</h3>
          <button onClick={() => { const m = mes === 12 ? 1 : mes+1; const a = mes === 12 ? ano+1 : ano; setMesAtual(`${a}-${String(m).padStart(2,'0')}`); }} style={cs.calNav}><FaChevronRight /></button>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>{['Dom','Seg','Ter','Qua','Qui','Sex','Sáb'].map(d => <th key={d} style={cs.calHead}>{d}</th>)}</tr></thead>
          <tbody>{rows}</tbody>
        </table>
        <div style={{ display: 'flex', gap: 16, marginTop: 12, fontSize: 12, color: '#999' }}>
          <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: '#d4807c', marginRight: 4 }}/>Hoje</span>
          <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: '#fef3f0', marginRight: 4 }}/>Com encomendas</span>
        </div>

        {/* Cards do dia selecionado */}
        {diaSelecionado && pedidosDia.length > 0 && (
          <div style={{ ...t.card, marginTop: 20, padding: '20px 24px' }}>
            <h3 style={{ ...t.h3, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
              <FaCalendarAlt style={{ color: theme.colors.primary }} />
              Encomendas para {new Date(diaSelecionado + 'T12:00:00').toLocaleDateString('pt-BR', { day: 'numeric', month: 'long' })}
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {pedidosDia.map(p => (
                <div key={p.id} style={cs.card}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                        <div style={{ fontWeight: 700, fontSize: 17, color: theme.colors.text }}>{p.cliente_nome}</div>
                        <div style={{ fontSize: 12, color: theme.colors.textMuted, fontWeight: 600 }}>#{p.id}</div>
                        {p.data_entrega && (
                          <div style={{ fontSize: 12, color: theme.colors.textMuted }}>
                            · {formatarData(p.data_entrega)}{p.horario_entrega ? ` ${p.horario_entrega.slice(0,5)}` : ''}
                          </div>
                        )}
                      </div>
                      <div style={{ fontSize: 13, color: theme.colors.textMuted, marginTop: 2 }}>
                        {p.itens?.map(i => i.tipo === 'bolo' ? `${i.tamanho_nome || 'Bolo'}` : i.descricao_manual).filter(Boolean).join(', ') || 'Sem itens'}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 2 }}>
                      <button onClick={() => abrirDetalhe(p.id)} style={{ ...cs.iconBtn, color: theme.colors.textMuted }} title="Detalhes"><FaEye /></button>
                      {!p.pago_completo && p.valor_total > 0 && (
                        <button onClick={() => setPagamentoPara(p)} style={{ ...cs.iconBtn, color: '#27ae60' }} title="Registrar pagamento">
                          <FaDollarSign />
                        </button>
                      )}
                      {p.pago_completo && <button style={{ ...cs.iconBtn, color: '#27ae60' }} title="Pago"><FaCheck /></button>}
                      <button onClick={() => { setEditandoPedido(p); setShowForm(true); }} style={{ ...cs.iconBtn, color: theme.colors.textMuted }} title="Editar"><FaEdit /></button>
                      <button onClick={() => deletarPedido(p.id)} style={{ ...cs.iconBtn, color: theme.colors.danger }} title="Excluir"><FaTrash /></button>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    <select value={p.status} onChange={e => mudarStatus(p.id, e.target.value)}
                      style={{ padding: '5px 28px 5px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700, border: `2px solid ${STATUS_CORES[p.status]}`, cursor: 'pointer', appearance: 'none', background: `${STATUS_CORES[p.status]}15`, color: STATUS_CORES[p.status], backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='${encodeURIComponent(STATUS_CORES[p.status])}'%3E%3Cpath d='M7 10l5 5 5-5z'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 8px center' }}>
                      {Object.entries(STATUS_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                    {p.pago_completo ? (
                      <span style={{ fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 20, background: '#eafaf1', color: '#27ae60' }}>✓ Pago {formatarMoeda(p.valor_pago)}</span>
                    ) : p.valor_pago > 0 ? (<>
                      <span style={{ fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 20, background: '#fef9e7', color: '#d4a017' }}>Sinal: {formatarMoeda(p.valor_pago)}</span>
                      <span style={{ fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 20, background: '#fdeded', color: '#c0392b' }}>Falta: {formatarMoeda(p.valor_restante)}</span>
                    </>) : p.valor_total > 0 ? (
                      <span style={{ fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 20, background: '#e74c3c', color: '#fff' }}>A receber: {formatarMoeda(p.valor_total)}</span>
                    ) : null}
                  </div>
                  {p.cliente_telefone && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                      <span style={{ fontSize: 13, color: theme.colors.textSecondary }}>📞 {formatarTelefone(p.cliente_telefone)}</span>
                    </div>
                  )}
                  <div style={{ marginTop: 12, fontSize: 13, background: theme.colors.bgSection, borderRadius: 12, padding: '10px 14px' }}>
                    {p.itens?.map(i => (
                      <div key={i.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', color: theme.colors.text }}>
                        <span>{i.tipo === 'bolo' ? `${i.tamanho_nome || 'Bolo'} x${i.quantidade}` : i.descricao_manual}</span>
                        <span style={{ fontWeight: 600 }}>{formatarMoeda((i.preco_unitario||0) * (i.quantidade||1))}</span>
                      </div>
                    ))}
                    {(p.taxa_entrega || 0) > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', color: theme.colors.textMuted }}>
                        <span>Taxa de Entrega</span><span>{formatarMoeda(p.taxa_entrega)}</span>
                      </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, marginTop: 6, paddingTop: 6, borderTop: `1px solid ${theme.colors.border}`, color: theme.colors.primary, fontSize: 14 }}>
                      <span>Total</span><span>{formatarMoeda(p.valor_total)}</span>
                    </div>
                  </div>
                  {p.notas && <div style={{ fontSize: 13, color: theme.colors.textMuted, marginTop: 8, fontStyle: 'italic' }}>{p.notas}</div>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 60, color: '#999' }}>Carregando...</div>;

  return (
    <PageLayout title="Pedidos">
      {/* Header actions */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: 20, marginTop: -16, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div style={{ display: 'flex', borderRadius: 10, overflow: 'hidden', border: '1px solid #eee' }}>
            <button onClick={() => { setView('lista'); setDiaSelecionado(null); setPedidosDia([]); }} style={{ ...cs.viewBtn, ...(view === 'lista' ? cs.viewActive : {}) }}><FaList /> Lista</button>
            <button onClick={() => { setView('calendario'); setDiaSelecionado(null); setPedidosDia([]); }} style={{ ...cs.viewBtn, ...(view === 'calendario' ? cs.viewActive : {}) }}><FaCalendarAlt /> Calendário</button>
          </div>
          <button onClick={() => { setEditandoPedido(null); setShowForm(true); }} style={cs.btnPrimary}><FaPlus /> Novo Pedido</button>
        </div>
      </div>

      {/* Calendário */}
      {view === 'calendario' && renderCalendario()}

      {/* Lista */}
      {view === 'lista' && (
        <>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
            <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
              <FaSearch style={{ position: 'absolute', left: 12, top: 11, color: '#bbb' }} />
              <input placeholder="Pesquisar pedido..." value={busca} onChange={e => setBusca(e.target.value)} style={{ ...cs.input, paddingLeft: 36 }} />
            </div>
          </div>

          {diaSelecionado && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, fontSize: 14, color: '#666' }}>
              Filtrando: <strong>{formatarData(diaSelecionado)}</strong>
              <button onClick={() => { setDiaSelecionado(null); }} style={{ ...cs.btnSmall, background: '#eee', color: '#666' }}><FaTimes /> Limpar</button>
            </div>
          )}

          {!diaSelecionado && (
            <div style={{ display: 'flex', gap: 0, marginBottom: 16, borderRadius: 10, overflow: 'hidden', border: '1px solid #eee' }}>
              {[['ativos', 'Ativos'], ['arquivados', 'Arquivados']].map(([k, label]) => (
                <button key={k} onClick={() => setAba(k)}
                  style={{ flex: 1, padding: '10px 0', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 600, background: aba === k ? '#faf7f5' : '#fff', color: aba === k ? theme.colors.primary : '#999', borderBottom: aba === k ? `2px solid ${theme.colors.primary}` : '2px solid transparent' }}>
                  {label}
                </button>
              ))}
            </div>
          )}

          {/* Aba ativos: cards agrupados por data */}
          {aba === 'ativos' && (
            pedidos.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#ccc' }}>Nenhum pedido encontrado.</div>
            ) : (() => {
              const grupos = {};
              pedidos.forEach(p => {
                const k = p.data_entrega || '__sem_data__';
                if (!grupos[k]) grupos[k] = [];
                grupos[k].push(p);
              });
              const hoje = new Date(); hoje.setHours(0,0,0,0);
              const hojeStr = `${hoje.getFullYear()}-${String(hoje.getMonth()+1).padStart(2,'0')}-${String(hoje.getDate()).padStart(2,'0')}`;
              const amanha = new Date(hoje); amanha.setDate(amanha.getDate()+1);
              const amanhaStr = `${amanha.getFullYear()}-${String(amanha.getMonth()+1).padStart(2,'0')}-${String(amanha.getDate()).padStart(2,'0')}`;
              const chaves = Object.keys(grupos).filter(k => k !== '__sem_data__').sort();
              const ordenadas = [];
              if (grupos[hojeStr]) ordenadas.push(hojeStr);
              if (grupos[amanhaStr]) ordenadas.push(amanhaStr);
              chaves.forEach(k => { if (k !== hojeStr && k !== amanhaStr) ordenadas.push(k); });
              if (grupos['__sem_data__']) ordenadas.push('__sem_data__');

              const tituloGrupo = (k) => {
                if (k === '__sem_data__') return 'Sem data';
                if (k === hojeStr) return 'Hoje';
                if (k === amanhaStr) return 'Amanhã';
                const d = new Date(k + 'T00:00:00');
                const s = d.toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'short' });
                return s.charAt(0).toUpperCase() + s.slice(1);
              };

              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                  {ordenadas.map(k => {
                    const lista = grupos[k];
                    const total = lista.reduce((s, p) => s + (p.valor_total || 0), 0);
                    return (
                      <div key={k}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '6px 4px 10px', borderBottom: `1px solid ${theme.colors.borderLight}`, marginBottom: 10 }}>
                          <div style={{ fontWeight: 700, fontSize: 14, color: theme.colors.text }}>{tituloGrupo(k)}</div>
                          <div style={{ fontSize: 12, color: theme.colors.textMuted }}>{lista.length} {lista.length === 1 ? 'venda' : 'vendas'} - {formatarMoeda(total)}</div>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 12 }}>
                          {lista.map(p => {
                            const arquivando = arquivosPendentes.some(a => a.id === p.id);
                            if (arquivando) return null;
                            return (
                              <PedidoCard
                                key={p.id}
                                pedido={p}
                                onAbrir={() => abrirDetalhe(p.id)}
                                onAvancar={() => avancarStatus(p)}
                                onMenuStatus={() => setMenuStatusPara(p)}
                                onCancelar={() => cancelarPedido(p)}
                                onEditar={() => { setEditandoPedido(p); setShowForm(true); }}
                                onPagamento={() => setPagamentoPara(p)}
                              />
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()
          )}

          {/* Aba arquivados */}
          {aba === 'arquivados' && (
            pedidosArquivados.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#ccc' }}>Nenhum pedido arquivado.</div>
            ) : (() => {
              const grupos = {};
              pedidosArquivados.forEach(p => {
                const dt = p.arquivado_em ? new Date(p.arquivado_em) : null;
                const k = dt ? `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,'0')}` : '__sem__';
                if (!grupos[k]) grupos[k] = [];
                grupos[k].push(p);
              });
              const meses = ['janeiro','fevereiro','março','abril','maio','junho','julho','agosto','setembro','outubro','novembro','dezembro'];
              const chaves = Object.keys(grupos).sort().reverse();
              const titulo = (k) => {
                if (k === '__sem__') return 'Sem data';
                const [a, m] = k.split('-').map(Number);
                return `${meses[m-1]} ${a}`;
              };
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                  {chaves.map(k => (
                    <div key={k}>
                      <div style={{ fontWeight: 700, fontSize: 14, color: theme.colors.text, padding: '6px 4px 10px', borderBottom: `1px solid ${theme.colors.borderLight}`, marginBottom: 10, textTransform: 'capitalize' }}>{titulo(k)}</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {grupos[k].map(p => {
                          const meta = STATUS_META[p.status] || STATUS_META.entregue;
                          return (
                            <div key={p.id} onClick={() => abrirDetalhe(p.id)} style={{ ...cs.card, cursor: 'pointer', padding: '12px 16px', display: 'flex', gap: 12, alignItems: 'center' }}>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 600, fontSize: 14 }}>{p.cliente_nome}</div>
                                <div style={{ fontSize: 12, color: theme.colors.textMuted, marginTop: 2 }}>
                                  #{p.id} · {formatarMoeda(p.valor_total)}
                                  {p.data_entrega && ` · ${formatarData(p.data_entrega)}`}
                                </div>
                              </div>
                              <span style={{ fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 12, background: '#eee', color: '#666' }}>{meta.label}</span>
                              <button onClick={(e) => { e.stopPropagation(); restaurarArquivado(p.id); }}
                                style={{ ...cs.btnSmall, background: '#fff', color: theme.colors.primary, border: `1px solid ${theme.colors.primary}` }}>
                                Restaurar
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()
          )}

        </>
      )}

      {/* Modal Form */}
      {showForm && (
        <PedidoFormModal
          pedido={editandoPedido}
          clientes={clientes}
          tamanhos={tamanhos}
          recheios={recheios}
          coberturas={coberturas}
          fichasMassa={fichasMassa}
          menuFixo={menuFixo}
          onClose={() => setShowForm(false)}
          onSave={(data, isEdit) => {
            setShowForm(false);
            if (isEdit) setPedidos(prev => prev.map(p => p.id === data.id ? data : p));
            else setPedidos(prev => Array.isArray(prev) ? [data, ...prev] : [data]);
            // Refetch para garantir sincronização com filtros e backend
            carregarPedidos();
            // Atualiza o resumo do dia selecionado, se houver
            if (diaSelecionado) carregarPedidosDia(diaSelecionado);
            if (view === 'calendario') carregarCalendario();
          }}
        />
      )}

      {/* Modal Detalhe */}
      {detalhePedido && (
        <PedidoDetailModal
          pedido={detalhePedido}
          onClose={() => setDetalhePedido(null)}
          onRefresh={() => abrirDetalhe(detalhePedido.id)}
          onStatusChange={(id, s) => { mudarStatus(id, s); abrirDetalhe(id); }}
          onAddPagamento={(p) => setPagamentoPara(p)}
          onCancelar={(p) => { cancelarPedido(p); setDetalhePedido(null); }}
        />
      )}

      {/* Menu de pular status */}
      {menuStatusPara && (
        <div style={cs.overlay} {...overlayCloseProps(() => setMenuStatusPara(null))}>
          <div style={{ ...cs.modal, maxWidth: 320 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Mudar status #{menuStatusPara.id}</h3>
              <button onClick={() => setMenuStatusPara(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#bbb' }}><FaTimes /></button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {STATUS_FLOW.map(s => {
                const meta = STATUS_META[s];
                const sel = menuStatusPara.status === s;
                return (
                  <button key={s}
                    onClick={async () => {
                      const id = menuStatusPara.id;
                      const statusAnterior = menuStatusPara.status;
                      const restante = menuStatusPara.valor_restante || 0;
                      if (s === 'entregue' && restante > 0) {
                        await confirm(`Pagamento pendente: falta ${formatarMoeda(restante)}. Registre o pagamento antes de marcar como retirado.`,
                          { confirmText: 'Entendi', danger: false });
                        return;
                      }
                      setMenuStatusPara(null);
                      await mudarStatus(id, s);
                      if (s === 'entregue') agendarArquivo(id, statusAnterior);
                    }}
                    style={{
                      padding: '10px 14px', borderRadius: 8, border: `2px solid ${meta.cor}`,
                      background: sel ? meta.cor : '#fff', color: sel ? '#fff' : meta.cor,
                      fontWeight: 700, fontSize: 13, cursor: 'pointer', textAlign: 'left'
                    }}>
                    {meta.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Toasts de arquivamento com countdown */}
      <ArquivoCountdownToast itens={arquivosPendentes} onDesfazer={desfazerArquivo} />


      {/* Modal Pagamento */}
      {pagamentoPara && (
        <PagamentoModal
          pedido={pagamentoPara}
          onClose={() => setPagamentoPara(null)}
          onSaved={() => {
            setPagamentoPara(null);
            carregarPedidos();
            if (diaSelecionado) carregarPedidosDia(diaSelecionado);
            if (detalhePedido) abrirDetalhe(detalhePedido.id);
          }}
        />
      )}
    </PageLayout>
  );
};


// ===========================================================================
// Pedido Card (lista ativa)
// ===========================================================================
const PedidoCard = ({ pedido, onAbrir, onAvancar, onMenuStatus, onCancelar, onEditar, onPagamento }) => {
  const meta = STATUS_META[pedido.status] || STATUS_META.confirmado;
  const podeAvancar = !!meta.next;
  const [hover, setHover] = useState(false);
  const proximoMeta = meta.next ? STATUS_META[meta.next] : null;
  const isEntrega = !!pedido.endereco_entrega;
  const labelTipo = isEntrega ? 'Entrega' : 'Retirada';
  const iniciaisStr = (pedido.cliente_nome || '?').split(' ').filter(Boolean).slice(0, 2).map(s => s[0].toUpperCase()).join('');
  const formatarRel = (dataIso) => {
    if (!dataIso) return '';
    const hoje = new Date(); hoje.setHours(0,0,0,0);
    const d = new Date(dataIso + 'T00:00:00');
    const diff = Math.round((d - hoje) / 86400000);
    if (diff === 0) return 'hoje';
    if (diff === 1) return 'amanhã';
    return d.toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'short' });
  };
  const fmt = (v) => `R$ ${(v||0).toFixed(2).replace('.',',')}`;
  const pago = (pedido.valor_restante || 0) === 0 && (pedido.valor_total || 0) > 0;

  return (
    <div onClick={onAbrir} style={{
      ...cs.card, cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 10,
      padding: 14, transition: 'box-shadow 150ms ease',
    }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <div style={{ width: 36, height: 36, borderRadius: '50%', background: meta.cor + '20', color: meta.cor, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13, flexShrink: 0 }}>
          {iniciaisStr}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 14, color: theme.colors.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{pedido.cliente_nome}</div>
          <div style={{ fontSize: 12, color: theme.colors.textMuted, display: 'flex', alignItems: 'center', gap: 6, marginTop: 2, flexWrap: 'wrap' }}>
            <span>#{pedido.id}</span>
            <span>·</span>
            <span style={{ color: pago ? '#27ae60' : '#f39c12', fontWeight: 600 }}>{fmt(pedido.valor_total)}</span>
            {!pago && (pedido.valor_total || 0) > 0 && (
              <button onClick={(e) => { e.stopPropagation(); onPagamento(); }} title="Registrar pagamento"
                style={{ background: 'none', border: 'none', color: '#27ae60', cursor: 'pointer', padding: 2, display: 'inline-flex', fontSize: 13 }}>
                <FaDollarSign />
              </button>
            )}
            {(pedido.valor_restante || 0) > 0 && (
              <span style={{ color: '#c0392b', fontSize: 11 }}>· falta {fmt(pedido.valor_restante)}</span>
            )}
          </div>
        </div>
      </div>

      <div style={{ fontSize: 12, color: theme.colors.textMuted }}>
        {pedido.data_entrega
          ? `${labelTipo} agendada para ${formatarRel(pedido.data_entrega)}${pedido.horario_entrega ? ' às ' + pedido.horario_entrega.slice(0,5) : ''}`
          : 'Sem data definida'}
      </div>

      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'nowrap' }}>
        <button
          onClick={(e) => { e.stopPropagation(); if (podeAvancar) onAvancar(); }}
          onMouseEnter={() => podeAvancar && setHover(true)}
          onMouseLeave={() => setHover(false)}
          disabled={!podeAvancar}
          title={podeAvancar ? `Marcar ${meta.proximoLabel}` : meta.label}
          style={{
            flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            padding: '7px 12px', borderRadius: 20, border: 'none',
            background: hover && proximoMeta ? proximoMeta.cor : meta.cor, color: '#fff',
            fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em',
            cursor: podeAvancar ? 'pointer' : 'default',
            opacity: podeAvancar ? 1 : 0.7,
            transition: 'background 180ms ease',
            whiteSpace: 'nowrap', overflow: 'hidden',
          }}>
          {hover && proximoMeta ? `→ ${proximoMeta.label}` : meta.label}
          {podeAvancar && !hover && <FaChevronRight style={{ fontSize: 9 }} />}
        </button>
        <button onClick={(e) => { e.stopPropagation(); onMenuStatus(); }} title="Mudar status"
          style={iconBtnStyle()}>
          <FaExchangeAlt style={{ fontSize: 12 }} />
        </button>
        {pedido.status !== 'cancelado' && pedido.status !== 'entregue' && (
          <button onClick={(e) => { e.stopPropagation(); onCancelar(); }} title="Cancelar pedido"
            style={iconBtnStyle('#e74c3c')}>
            <FaTrash style={{ fontSize: 12 }} />
          </button>
        )}
        <button onClick={(e) => { e.stopPropagation(); onEditar(); }} title="Editar"
          style={iconBtnStyle()}>
          <FaEdit style={{ fontSize: 12 }} />
        </button>
      </div>
    </div>
  );
};

const iconBtnStyle = (color) => ({
  width: 32, height: 32, padding: 0, borderRadius: 8,
  border: '1px solid #eee', background: '#fff',
  color: color || '#888', cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  flexShrink: 0,
});


// ===========================================================================
// Arquivo Countdown Toast
// ===========================================================================
const ArquivoCountdownToast = ({ itens, onDesfazer }) => {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (itens.length === 0) return;
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [itens.length]);
  if (!itens || itens.length === 0) return null;
  return (
    <div style={{ position: 'fixed', bottom: 20, right: 20, zIndex: 1000, display: 'flex', flexDirection: 'column', gap: 8 }}>
      {itens.map(arq => {
        const segundos = Math.max(0, Math.ceil((arq.deadline_ts - Date.now()) / 1000));
        return (
          <div key={arq.id} style={{ background: '#2c3e50', color: '#fff', padding: '12px 16px', borderRadius: 10, boxShadow: '0 4px 16px rgba(0,0,0,0.2)', display: 'flex', alignItems: 'center', gap: 12, minWidth: 280 }}>
            <span style={{ fontSize: 13 }}>Pedido #{arq.id} enviado para o arquivo em {segundos}s</span>
            <button onClick={() => onDesfazer(arq.id)}
              style={{ background: 'none', border: 'none', color: '#f39c12', fontWeight: 700, cursor: 'pointer', fontSize: 13, marginLeft: 'auto' }}>
              Desfazer
            </button>
          </div>
        );
      })}
    </div>
  );
};


// ===========================================================================
// Form Modal
// ===========================================================================
const PedidoFormModal = ({ pedido, clientes: clientesProp, tamanhos, recheios, fichasMassa, coberturas = [], menuFixo = [], onClose, onSave }) => {
  const [clientesLocais, setClientesLocais] = useState(clientesProp);
  const clientes = clientesLocais;
  const [clienteId, setClienteId] = useState(pedido?.cliente_id || '');
  const [buscaCliente, setBuscaCliente] = useState('');
  const [itens, setItens] = useState(pedido?.itens?.map(i => {
    const rIds = [i.recheio_principal_id ? String(i.recheio_principal_id) : ''];
    if (i.recheio_secundario_id && i.recheio_secundario_id !== i.recheio_principal_id) rIds.push(String(i.recheio_secundario_id));
    if (i.recheio_terciario_id) rIds.push(String(i.recheio_terciario_id));
    return {...i, recheios: rIds.length ? rIds : ['']};
  }) || [
    { tipo: 'bolo', tamanho_id: '', massa_ficha_id: '', recheios: [''], cobertura: '', decoracao: '', descricao_manual: '', quantidade: 1, preco_unitario: '' }
  ]);
  const [dataEntrega, setDataEntrega] = useState(pedido?.data_entrega || '');
  const [horarioEntrega, setHorarioEntrega] = useState(pedido?.horario_entrega || '');
  const [tipoEntrega, setTipoEntrega] = useState(pedido?.endereco_entrega ? 'entrega' : 'retirada');
  const [enderecoEntrega, setEnderecoEntrega] = useState(pedido?.endereco_entrega || '');
  const [taxaEntrega, setTaxaEntrega] = useState(pedido?.taxa_entrega?.toString() || '');
  const [descontoAtivo, setDescontoAtivo] = useState((pedido?.desconto || 0) > 0);
  const [descontoTipo, setDescontoTipo] = useState('reais'); // reais | percentual
  const [desconto, setDesconto] = useState(pedido?.desconto?.toString() || '');
  const [status] = useState(pedido?.status || 'confirmado');
  const [notas, setNotas] = useState(pedido?.notas || '');
  const hoje = new Date().toISOString().split('T')[0];
  const [modoPagamento, setModoPagamento] = useState(
    pedido?.pagamentos?.some(p => p.tipo === 'pagamento') ? 'pago' :
    pedido?.pagamentos?.some(p => p.tipo === 'sinal') ? 'sinal' : ''
  );
  const [sinalValor, setSinalValor] = useState((() => { const v = pedido?.pagamentos?.find(p => p.tipo === 'sinal')?.valor; return v ? formatarMoedaInput(parseFloat(v).toFixed(2).replace('.', '')) : ''; })());
  const [sinalMetodo, setSinalMetodo] = useState(pedido?.pagamentos?.find(p => p.tipo === 'sinal')?.metodo || 'pix');
  const [sinalData, setSinalData] = useState(pedido?.pagamentos?.find(p => p.tipo === 'sinal')?.data || hoje);
  const [pagoMetodo, setPagoMetodo] = useState(pedido?.pagamentos?.find(p => p.tipo === 'pagamento')?.metodo || 'pix');
  const [pagoData, setPagoData] = useState(pedido?.pagamentos?.find(p => p.tipo === 'pagamento')?.data || hoje);
  const [erro, setErro] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [showNovoCliente, setShowNovoCliente] = useState(false);
  const [novoClienteNome, setNovoClienteNome] = useState('');
  const [novoClienteTelefone, setNovoClienteTelefone] = useState('');
  const [novoClienteErro, setNovoClienteErro] = useState(null);

  const criarClienteInline = async () => {
    if (!novoClienteNome.trim() || !novoClienteTelefone.trim()) {
      setNovoClienteErro('Nome e telefone são obrigatórios.');
      return;
    }
    setNovoClienteErro(null);
    try {
      const res = await api.post('/api/clientes', { nome: novoClienteNome.trim(), telefone: novoClienteTelefone.trim() });
      setClientesLocais([...clientes, res.data]);
      setClienteId(res.data.id);
      setShowNovoCliente(false);
      setNovoClienteNome('');
      setNovoClienteTelefone('');
      setBuscaCliente('');
    } catch (err) {
      setNovoClienteErro(err.response?.data?.erro || 'Erro ao criar cliente.');
    }
  };

  const clientesFiltrados = buscaCliente
    ? clientes.filter(c => norm(c.nome).includes(norm(buscaCliente)) || c.telefone.includes(buscaCliente))
    : clientes;

  const addItem = (tipo) => {
    setItens([...itens, { tipo, tamanho_id: '', massa_ficha_id: '', recheios: [''], cobertura: '', decoracao: '', descricao_manual: '', quantidade: 1, preco_unitario: '' }]);
  };

  const buscarPrecoSugerido = (item) => {
    const tam = tamanhos.find(t => t.id === parseInt(item.tamanho_id));
    if (!tam) return null;
    // Congelado: tenta achar SKU do menu fixo (tamanho + massa + recheio principal)
    if (tam.tipo === 'congelado') {
      const massaId = parseInt(item.massa_ficha_id) || null;
      const rIds = (item.recheios || []).filter(Boolean).map(Number);
      const recheioId = rIds[0] || null;
      if (massaId && recheioId) {
        const sku = menuFixo.find(m =>
          m.tamanho_id === tam.id &&
          m.massa_ficha_id === massaId &&
          m.recheio_id === recheioId
        );
        if (sku?.preco) return sku.preco;
      }
    }
    return tam.preco || null;
  };

  const updateItem = (idx, field, value) => {
    const updated = [...itens];
    updated[idx] = { ...updated[idx], [field]: value };
    // Auto-preencher preço quando tamanho/massa/recheio mudam
    if (['tamanho_id', 'massa_ficha_id', 'recheios'].includes(field)) {
      const preco = buscarPrecoSugerido(updated[idx]);
      if (preco) updated[idx].preco_unitario = Number(preco).toFixed(2).replace('.', ',');
    }
    setItens(updated);
  };

  const removeItem = (idx) => setItens(itens.filter((_, i) => i !== idx));

  const valorProdutos = itens.reduce((s, i) => s + parseMoeda(String(i.preco_unitario)) * (parseInt(i.quantidade) || 1), 0);
  const descontoValor = !descontoAtivo ? 0 :
    descontoTipo === 'percentual' ? valorProdutos * ((parseFloat(desconto) || 0) / 100) : parseMoeda(desconto);
  const valorTotal = valorProdutos - descontoValor + (tipoEntrega === 'entrega' ? parseMoeda(taxaEntrega) : 0);

  /**
   * Leva a pessoa ate o problema. Sem isto, "Selecione um cliente" aparecia no
   * topo do modal enquanto ela olhava o botao la no rodape.
   */
  const irParaOProblema = (seletor) => {
    const alvo = document.querySelector(seletor);
    if (!alvo) return;
    alvo.scrollIntoView({ behavior: 'smooth', block: 'center' });
    if (typeof alvo.focus === 'function') setTimeout(() => alvo.focus({ preventScroll: true }), 300);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!clienteId) { setErro('Selecione um cliente.'); irParaOProblema('[data-campo="cliente"]'); return; }
    if (itens.length === 0) { setErro('Adicione pelo menos um item.'); irParaOProblema('[data-campo="produtos"]'); return; }
    setErro(null);
    setSalvando(true);

    const payload = {
      cliente_id: parseInt(clienteId),
      status,
      data_entrega: dataEntrega || null,
      horario_entrega: horarioEntrega || null,
      endereco_entrega: enderecoEntrega || null,
      taxa_entrega: tipoEntrega === 'entrega' ? parseMoeda(taxaEntrega) : 0,
      desconto: descontoValor,
      notas: notas || null,
      endereco_entrega: tipoEntrega === 'entrega' ? enderecoEntrega : null,
      itens: itens.map(i => ({
        tipo: i.tipo,
        tamanho_id: i.tamanho_id ? parseInt(i.tamanho_id) : null,
        massa_ficha_id: i.massa_ficha_id ? parseInt(i.massa_ficha_id) : null,
        recheio_principal_id: (i.recheios || [])[0] ? parseInt((i.recheios || [])[0]) : null,
        recheio_secundario_id: (i.recheios || [])[1] ? parseInt((i.recheios || [])[1]) : (i.recheios || [])[0] ? parseInt((i.recheios || [])[0]) : null,
        recheio_terciario_id: (i.recheios || [])[2] ? parseInt((i.recheios || [])[2]) : null,
        cobertura: i.cobertura || null,
        decoracao: i.decoracao || null,
        imagem_inspiracao: i.imagem_inspiracao || null,
        descricao_manual: i.descricao_manual || null,
        quantidade: parseInt(i.quantidade) || 1,
        preco_unitario: parseMoeda(String(i.preco_unitario)),
      })),
      pagamentos: [],
    };

    if (modoPagamento === 'pago') {
      payload.pagamentos.push({ tipo: 'pagamento', valor: valorTotal, metodo: pagoMetodo, data: pagoData || hoje });
    } else if (modoPagamento === 'sinal' && sinalValor) {
      payload.pagamentos.push({ tipo: 'sinal', valor: parseMoeda(sinalValor), metodo: sinalMetodo, data: sinalData || hoje });
    }

    try {
      let res;
      if (pedido?.id) { res = await api.put(`/api/pedidos/${pedido.id}`, payload); }
      else { res = await api.post('/api/pedidos', payload); }
      onSave(res.data, !!pedido?.id);
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao salvar.');
    } finally { setSalvando(false); }
  };

  // Recheios permitidos para um tamanho (cai para todos quando o tamanho não restringe).
  const getRecheiosDoTamanho = (tamanhoId) => {
    const t = tamanhos.find(x => String(x.id) === String(tamanhoId));
    if (!t?.recheios_permitidos?.length) return recheios;
    const ids = t.recheios_permitidos.map(r => r.recheio_id);
    return recheios.filter(r => ids.includes(r.id));
  };
  const getMassasDoTamanho = (tamanhoId) => {
    const t = tamanhos.find(t => t.id === parseInt(tamanhoId));
    if (!t?.massas_permitidas?.length) return fichasMassa;
    const ids = t.massas_permitidas.map(m => m.ficha_tecnica_id);
    return fichasMassa.filter(f => ids.includes(f.id));
  };

  return (
    <div style={cs.overlay} className="modal-overlay-mobile">
      <div style={{ ...cs.modal, maxWidth: 560, maxHeight: '92vh' }} className="modal-content-sheet">
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{pedido ? 'Editar Pedido' : 'Novo Pedido'}</h3>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#999' }}>Registre os detalhes do pedido</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#bbb' }}><FaTimes /></button>
        </div>

        {erro && <div style={cs.error}>{erro}</div>}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Cliente */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <label style={{ ...cs.label, margin: 0 }}>Cliente</label>
              {!showNovoCliente && (
                <button type="button" onClick={() => { setShowNovoCliente(true); setNovoClienteNome(buscaCliente); }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: '#d4807c', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <FaPlus style={{ fontSize: 10 }} /> Novo Cliente
                </button>
              )}
            </div>

            {showNovoCliente ? (
              <div style={{ background: '#faf7f5', borderRadius: 12, padding: 14 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#555', marginBottom: 8 }}>Cadastrar novo cliente</div>
                {novoClienteErro && <div style={{ ...cs.error, marginBottom: 8 }}>{novoClienteErro}</div>}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8, marginBottom: 8 }}>
                  <div>
                    <label style={{ fontSize: 12, color: '#888' }}>Nome *</label>
                    <input value={novoClienteNome} onChange={e => setNovoClienteNome(e.target.value)} placeholder="Maria Silva" style={cs.inputModal} autoFocus />
                  </div>
                  <div>
                    <label style={{ fontSize: 12, color: '#888' }}>Telefone *</label>
                    <TelefoneInput value={novoClienteTelefone} onChange={setNovoClienteTelefone} style={cs.inputModal} />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" onClick={criarClienteInline} style={cs.btnSmall}>Cadastrar</button>
                  <button type="button" onClick={() => setShowNovoCliente(false)} style={{ ...cs.btnSmall, background: '#eee', color: '#666' }}>Cancelar</button>
                </div>
              </div>
            ) : clienteId ? (
              (() => {
                const c = clientes.find(c => c.id === clienteId);
                return (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', border: `1px solid ${theme.colors.border}`, borderRadius: 10, background: '#fef9f8' }}>
                    <div style={{ fontSize: 14 }}>
                      <strong>{c?.nome || 'Cliente'}</strong>
                      {c?.telefone && <span style={{ color: '#888', marginLeft: 8 }}>{formatarTelefone(c.telefone)}</span>}
                    </div>
                    <button type="button" onClick={() => setClienteId('')}
                      title="Trocar cliente"
                      style={{ color: '#999', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14 }}>
                      <FaTimes />
                    </button>
                  </div>
                );
              })()
            ) : (
              <>
                <input data-campo="cliente" placeholder="Pesquisar clientes..." value={buscaCliente} onChange={e => setBuscaCliente(e.target.value)} style={cs.inputModal} />
                {buscaCliente && (
                  <div style={{ maxHeight: 150, overflow: 'auto', border: '1px solid #eee', borderRadius: 8, marginTop: 4 }}>
                    {clientesFiltrados.length > 0 ? clientesFiltrados.map(c => (
                      <div key={c.id} onClick={() => { setClienteId(c.id); setBuscaCliente(''); }}
                        style={{ padding: '8px 12px', cursor: 'pointer', background: '#fff', borderBottom: '1px solid #f5f5f5', fontSize: 14 }}>
                        <strong>{c.nome}</strong> <span style={{ color: '#999' }}>{formatarTelefone(c.telefone)}</span>
                      </div>
                    )) : (
                      <div style={{ padding: '12px', textAlign: 'center' }}>
                        <div style={{ color: '#999', fontSize: 13, marginBottom: 8 }}>Nenhum cliente encontrado</div>
                        <button type="button" onClick={() => { setShowNovoCliente(true); setNovoClienteNome(buscaCliente); }}
                          style={{ ...cs.btnSmall, background: '#d4807c' }}>
                          <FaPlus style={{ fontSize: 10 }} /> Cadastrar "{buscaCliente}"
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Produtos */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <label data-campo="produtos" style={cs.label}>Produtos</label>
              <div style={{ display: 'flex', gap: 6 }}>
                <button type="button" onClick={() => addItem('bolo')} style={cs.btnSmall}>Bolo</button>
                <button type="button" onClick={() => addItem('manual')} style={{ ...cs.btnSmall, background: '#eee', color: '#555' }}>+ Manual</button>
              </div>
            </div>

            {itens.map((item, idx) => (
              <div key={idx} style={{ background: '#faf7f5', borderRadius: 12, padding: 14, marginBottom: 10, position: 'relative' }}>
                <button type="button" onClick={() => removeItem(idx)} style={{ position: 'absolute', top: 8, right: 8, background: 'none', border: 'none', color: '#ccc', cursor: 'pointer' }}><FaTimes /></button>
                <div style={{ fontSize: 12, color: '#999', marginBottom: 8, fontWeight: 600 }}>Item {idx+1} — {item.tipo === 'bolo' ? 'Bolo' : 'Manual'}</div>

                {item.tipo === 'bolo' ? (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8, marginBottom: 8 }}>
                      <div>
                        <label style={{ fontSize: 12, color: '#888' }}>Tamanho</label>
                        <select value={item.tamanho_id} onChange={e => updateItem(idx, 'tamanho_id', e.target.value)} style={cs.inputModal}>
                          <option value="">Selecione...</option>
                          {tamanhos.map(t => <option key={t.id} value={t.id}>{t.nome} ({formatFatiasKg(t.fatias, t.tipo_medida)})</option>)}
                        </select>
                      </div>
                      <div>
                        <label style={{ fontSize: 12, color: '#888' }}>Massa</label>
                        <select value={item.massa_ficha_id} onChange={e => updateItem(idx, 'massa_ficha_id', e.target.value)} style={cs.inputModal}>
                          <option value="">Selecione...</option>
                          {getMassasDoTamanho(item.tamanho_id).map(f => <option key={f.id} value={f.id}>{f.nome}</option>)}
                        </select>
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8, marginBottom: 8 }}>
                      <div>
                        <label style={{ fontSize: 12, color: '#888' }}>Recheios</label>
                        {(item.recheios || ['']).map((rId, ri) => (
                          <div key={ri} style={{ display: 'flex', gap: 4, alignItems: 'center', marginBottom: 4 }}>
                            <span style={{ fontSize: 11, color: '#aaa', width: 16 }}>{ri + 1}</span>
                            <select value={rId} onChange={e => {
                              const novos = [...(item.recheios || [''])];
                              novos[ri] = e.target.value;
                              updateItem(idx, 'recheios', novos);
                            }} style={{ ...cs.inputModal, flex: 1 }}>
                              <option value="">Selecione...</option>
                              {getRecheiosDoTamanho(item.tamanho_id).map(r => <option key={r.id} value={r.id}>{r.nome}</option>)}
                            </select>
                            {ri > 0 && <button type="button" onClick={() => {
                              const novos = (item.recheios || []).filter((_, i) => i !== ri);
                              updateItem(idx, 'recheios', novos);
                            }} style={{ background: 'none', border: 'none', color: '#ccc', cursor: 'pointer', fontSize: 14 }}>✕</button>}
                          </div>
                        ))}
                        {(item.recheios || ['']).length < 3 && (
                          <button type="button" onClick={() => updateItem(idx, 'recheios', [...(item.recheios || ['']), ''])}
                            style={{ background: 'none', border: 'none', color: theme.colors.primary, fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0 }}>
                            + Adicionar recheio
                          </button>
                        )}
                      </div>
                      <div>
                        <label style={{ fontSize: 12, color: '#888' }}>Cobertura</label>
                        <select value={item.cobertura || ''} onChange={e => updateItem(idx, 'cobertura', e.target.value)} style={cs.inputModal}>
                          <option value="">Selecione...</option>
                          {coberturas.map(c => <option key={c.id} value={c.nome}>{c.nome}</option>)}
                        </select>
                      </div>
                    </div>
                    <div style={{ marginBottom: 8 }}>
                      <label style={{ fontSize: 12, color: '#888' }}>Decoração</label>
                      <input value={item.decoracao || ''} onChange={e => updateItem(idx, 'decoracao', e.target.value)} placeholder="Ex: Flores naturais, tema unicórnio..." style={cs.inputModal} />
                    </div>
                    <div style={{ marginBottom: 8 }}>
                      <label style={{ fontSize: 12, color: '#888' }}>Fotos de Referência (até 3)</label>
                      {(() => {
                        const fotos = (item.imagem_inspiracao || '').split(',').map(s => s.trim()).filter(Boolean);
                        const setFotos = (arr) => updateItem(idx, 'imagem_inspiracao', arr.join(','));
                        return (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 4, paddingTop: 8, paddingRight: 8 }}>
                            {fotos.map((url, i) => (
                              <div key={i} style={{ position: 'relative', width: 120, height: 120 }}>
                                <img src={url} alt={`Ref ${i + 1}`} style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 10, border: `1px solid ${theme.colors.border}` }} />
                                <button type="button" onClick={() => setFotos(fotos.filter((_, j) => j !== i))}
                                  title="Remover foto"
                                  style={{ position: 'absolute', top: -8, right: -8, width: 24, height: 24, borderRadius: '50%', background: theme.colors.danger, color: '#fff', border: '2px solid #fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 4px rgba(0,0,0,0.2)', padding: 0, lineHeight: 1, zIndex: 2 }}>
                                  <FaTimes size={10} />
                                </button>
                              </div>
                            ))}
                            {fotos.length < 3 && (
                              <label style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4, width: 120, height: 120, borderRadius: 10, border: `1.5px dashed ${theme.colors.border}`, cursor: 'pointer', color: theme.colors.textMuted, fontSize: 12 }}>
                                <FaCamera size={20} />
                                <span>{item._uploading ? 'Enviando...' : 'Adicionar foto'}</span>
                                <input type="file" accept="image/*" style={{ display: 'none' }} onChange={async (e) => {
                                  const file = e.target.files[0];
                                  if (!file) return;
                                  updateItem(idx, '_uploading', true);
                                  const url = await uploadCloudinary(file, `pedidos`);
                                  updateItem(idx, '_uploading', false);
                                  if (url) setFotos([...fotos, url].slice(0, 3));
                                }} />
                              </label>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                    <div>
                      <label style={{ fontSize: 12, color: '#888' }}>Preço (R$)</label>
                      <input value={typeof item.preco_unitario === 'number' ? item.preco_unitario.toFixed(2).replace('.',',') : item.preco_unitario} onChange={e => updateItem(idx, 'preco_unitario', formatarMoedaInput(e.target.value))} placeholder="0,00" style={cs.inputModal} />
                    </div>
                  </>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8 }}>
                    <div><label style={{ fontSize: 12, color: '#888' }}>Descrição</label><input value={item.descricao_manual || ''} onChange={e => updateItem(idx, 'descricao_manual', e.target.value)} placeholder="Ex: Kit doces" style={cs.inputModal} /></div>
                    <div><label style={{ fontSize: 12, color: '#888' }}>Qtd</label><input type="number" min="1" value={item.quantidade} onChange={e => updateItem(idx, 'quantidade', e.target.value)} style={cs.inputModal} /></div>
                    <div><label style={{ fontSize: 12, color: '#888' }}>Preço</label><input value={item.preco_unitario} onChange={e => updateItem(idx, 'preco_unitario', e.target.value)} placeholder="0,00" style={cs.inputModal} /></div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Data e Horário — a data precisa de mais largura que a hora: no iOS o
              campo mostra "21 de ago. de 2026" por extenso, e em duas colunas
              iguais (minmax(180px,1fr)) o conteudo estourava a trilha e vazava
              para fora do modal. minmax(0,..) permite a trilha encolher. */}
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.7fr) minmax(0, 1fr)', gap: 8 }}>
            <div style={{ minWidth: 0 }}><label style={cs.label}>Data de entrega</label><input type="date" value={dataEntrega} onChange={e => setDataEntrega(e.target.value)} style={{ ...cs.inputModal, maxWidth: '100%' }} /></div>
            <div style={{ minWidth: 0 }}><label style={cs.label}>Horário</label><input type="time" value={horarioEntrega} onChange={e => setHorarioEntrega(e.target.value)} style={{ ...cs.inputModal, maxWidth: '100%' }} /></div>
          </div>

          {/* Tipo entrega: retirada ou entrega */}
          <div>
            <label style={cs.label}>Entrega</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8 }}>
              <button type="button" onClick={() => setTipoEntrega('retirada')}
                style={{ ...cs.toggleBtn, ...(tipoEntrega === 'retirada' ? cs.toggleActive : {}) }}>
                Cliente vai retirar
              </button>
              <button type="button" onClick={() => setTipoEntrega('entrega')}
                style={{ ...cs.toggleBtn, ...(tipoEntrega === 'entrega' ? cs.toggleActive : {}) }}>
                Entrega pela loja
              </button>
            </div>
          </div>
          {tipoEntrega === 'entrega' && (
            <>
              <div><label style={cs.label}>Endereço de Entrega</label><input value={enderecoEntrega} onChange={e => setEnderecoEntrega(e.target.value)} placeholder="Rua das Flores, 123" style={cs.inputModal} /></div>
              <div><label style={cs.label}>Taxa de Entrega (R$)</label><input value={taxaEntrega} onChange={e => setTaxaEntrega(formatarMoedaInput(e.target.value))} placeholder="0,00" style={cs.inputModal} /></div>
            </>
          )}

          {/* Desconto */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: descontoAtivo ? 8 : 0 }}>
              <label style={{ ...cs.label, margin: 0 }}>Desconto:</label>
              <input type="checkbox" checked={descontoAtivo} onChange={e => setDescontoAtivo(e.target.checked)} style={{ width: 18, height: 18 }} />
            </div>
            {descontoAtivo && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <div style={{ display: 'inline-flex', borderRadius: 8, overflow: 'hidden', border: '1px solid #eee' }}>
                  <button type="button" onClick={() => setDescontoTipo('reais')}
                    style={{ padding: '6px 14px', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, background: descontoTipo === 'reais' ? '#d4807c' : '#fff', color: descontoTipo === 'reais' ? '#fff' : '#999' }}>
                    R$
                  </button>
                  <button type="button" onClick={() => setDescontoTipo('percentual')}
                    style={{ padding: '6px 14px', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, background: descontoTipo === 'percentual' ? '#d4807c' : '#fff', color: descontoTipo === 'percentual' ? '#fff' : '#999' }}>
                    %
                  </button>
                </div>
                <input value={desconto} onChange={e => setDesconto(descontoTipo === 'reais' ? formatarMoedaInput(e.target.value) : e.target.value.replace(/[^0-9]/g, ''))} placeholder={descontoTipo === 'reais' ? '0,00' : '0'} style={{ ...cs.inputModal, width: 100 }} />
                {descontoTipo === 'percentual' && descontoValor > 0 && (
                  <span style={{ fontSize: 12, color: '#888' }}>= R$ {descontoValor.toFixed(2).replace('.',',')}</span>
                )}
              </div>
            )}
          </div>

          {/* Notas */}
          <div><label style={cs.label}>Notas</label><textarea value={notas} onChange={e => setNotas(e.target.value)} placeholder="Observações especiais..." style={{ ...cs.inputModal, minHeight: 60, resize: 'vertical' }} /></div>

          {/* Pagamento */}
          <div style={{ background: '#faf7f5', borderRadius: 12, padding: 14 }}>
            <label style={{ ...cs.label, marginBottom: 10 }}>Pagamento</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8, marginBottom: modoPagamento ? 12 : 0 }}>
              <button type="button" onClick={() => setModoPagamento(modoPagamento === 'pago' ? '' : 'pago')}
                style={{ ...cs.toggleBtn, ...(modoPagamento === 'pago' ? { background: '#27ae60', color: '#fff', borderColor: '#27ae60' } : {}) }}>
                Já foi 100% pago
              </button>
              <button type="button" onClick={() => setModoPagamento(modoPagamento === 'sinal' ? '' : 'sinal')}
                style={{ ...cs.toggleBtn, ...(modoPagamento === 'sinal' ? cs.toggleActive : {}) }}>
                Sinal / Entrada
              </button>
            </div>

            {modoPagamento === 'pago' && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8 }}>
                <div><label style={{ fontSize: 12, color: '#888' }}>Método</label>
                  <select value={pagoMetodo} onChange={e => setPagoMetodo(e.target.value)} style={cs.inputModal}>
                    {METODOS.map(m => <option key={m} value={m}>{METODO_LABEL[m]}</option>)}
                  </select>
                </div>
                <div><label style={{ fontSize: 12, color: '#888' }}>Data</label>
                  <input type="date" value={pagoData} onChange={e => setPagoData(e.target.value)} style={cs.inputModal} />
                </div>
              </div>
            )}

            {modoPagamento === 'sinal' && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8 }}>
                <div><label style={{ fontSize: 12, color: '#888' }}>Valor (R$)</label><input value={sinalValor} onChange={e => setSinalValor(formatarMoedaInput(e.target.value))} placeholder="0,00" style={cs.inputModal} /></div>
                <div><label style={{ fontSize: 12, color: '#888' }}>Método</label>
                  <select value={sinalMetodo} onChange={e => setSinalMetodo(e.target.value)} style={cs.inputModal}>
                    {METODOS.map(m => <option key={m} value={m}>{METODO_LABEL[m]}</option>)}
                  </select>
                </div>
                <div><label style={{ fontSize: 12, color: '#888' }}>Data</label><input type="date" value={sinalData} onChange={e => setSinalData(e.target.value)} style={cs.inputModal} /></div>
              </div>
            )}
          </div>

          {/* Total */}
          <div style={{ textAlign: 'right', fontSize: 18, fontWeight: 700, color: '#d4807c', padding: '8px 0' }}>
            Total: R$ {valorTotal.toFixed(2).replace('.',',')}
          </div>

          {/* O aviso tem que estar ONDE A PESSOA TOCOU. O banner la em cima
              fica fora da tela num formulario deste tamanho: o toque parecia
              nao fazer nada, e o erro so aparecia se ela rolasse de volta. */}
          {erro && (
            <div style={{ ...cs.error, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span aria-hidden="true">⚠️</span>
              <span>{erro}</span>
            </div>
          )}
          <button type="submit" disabled={salvando} style={{ ...cs.btnPrimary, width: '100%', justifyContent: 'center', padding: '12px 0' }}>
            {salvando ? 'Salvando...' : pedido ? 'Salvar Alterações' : 'Criar Pedido'}
          </button>
        </form>
      </div>
    </div>
  );
};


// ===========================================================================
// Detail Modal
// ===========================================================================
const PagamentoModal = ({ pedido, onClose, onSaved }) => {
  const formatarMoeda = (v) => `R$ ${(v||0).toFixed(2).replace('.',',')}`;
  const hojeISO = new Date().toISOString().split('T')[0];
  const [valor, setValor] = useState((pedido.valor_restante || 0).toFixed(2).replace('.', ','));
  const [metodo, setMetodo] = useState('pix');
  const [data, setData] = useState(hojeISO);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  const salvar = async () => {
    const v = parseFloat(String(valor).replace(/\./g, '').replace(',', '.'));
    if (!v || v <= 0) { setErro('Informe um valor válido'); return; }
    setSalvando(true); setErro('');
    try {
      const tipo = (pedido.valor_pago || 0) === 0 && Math.abs(v - (pedido.valor_total || 0)) > 0.01 ? 'sinal' : 'pagamento';
      await api.post(`/api/pedidos/${pedido.id}/pagamentos`, { tipo, valor: v, metodo, data });
      onSaved && onSaved();
    } catch (e) {
      setErro(e.response?.data?.erro || 'Erro ao registrar pagamento');
    } finally { setSalvando(false); }
  };

  return (
    <div style={cs.overlay} {...overlayCloseProps(onClose)} className="modal-overlay-mobile">
      <div style={{ ...cs.modal, maxWidth: 380 }} className="modal-content-sheet">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Registrar pagamento</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#bbb' }}><FaTimes /></button>
        </div>
        {erro && <div style={{ background: '#fdeded', color: '#c0392b', padding: 8, borderRadius: 8, marginBottom: 10, fontSize: 13 }}>{erro}</div>}
        <div style={{ fontSize: 13, color: '#888', marginBottom: 12 }}>
          Total: <strong>{formatarMoeda(pedido.valor_total)}</strong>
          {(pedido.valor_pago || 0) > 0 && <> · Pago: <strong>{formatarMoeda(pedido.valor_pago)}</strong></>}
          {(pedido.valor_restante || 0) > 0 && <> · Falta: <strong style={{ color: '#c0392b' }}>{formatarMoeda(pedido.valor_restante)}</strong></>}
        </div>
        <div style={{ marginBottom: 10 }}>
          <label style={{ fontSize: 12, color: '#888' }}>Valor (R$)</label>
          <input value={valor} onChange={e => setValor(formatarMoedaInput(e.target.value))} placeholder="0,00" style={cs.inputModal} autoFocus />
        </div>
        <div style={{ marginBottom: 10 }}>
          <label style={{ fontSize: 12, color: '#888' }}>Método</label>
          <select value={metodo} onChange={e => setMetodo(e.target.value)} style={cs.inputModal}>
            <option value="pix">PIX</option>
            <option value="dinheiro">Dinheiro</option>
            <option value="credito">Cartão de crédito</option>
            <option value="debito">Cartão de débito</option>
            <option value="transferencia">Transferência</option>
            <option value="outro">Outro</option>
          </select>
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 12, color: '#888' }}>Data</label>
          <input type="date" value={data} onChange={e => setData(e.target.value)} style={cs.inputModal} />
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose} style={{ ...cs.btnSmall, background: '#eee', color: '#666' }}>Cancelar</button>
          <button type="button" onClick={salvar} disabled={salvando} style={{ ...cs.btnSmall, background: '#27ae60', color: '#fff' }}>
            {salvando ? 'Salvando...' : 'Registrar'}
          </button>
        </div>
      </div>
    </div>
  );
};

const PedidoDetailModal = ({ pedido, onClose, onRefresh, onStatusChange, onAddPagamento, onCancelar }) => {
  const confirm = useConfirm();
  const formatarMoeda = (v) => `R$ ${(v||0).toFixed(2).replace('.',',')}`;
  const formatarData = (d) => d ? new Date(d+'T00:00:00').toLocaleDateString('pt-BR') : '';

  const abrirWhatsApp = () => {
    if (!pedido.cliente_telefone) return;
    const num = pedido.cliente_telefone.replace(/\D/g, '');
    const itensTexto = pedido.itens?.map(i => i.tipo === 'bolo' ? `${i.tamanho_nome} - ${i.recheio_principal_nome || ''}` : i.descricao_manual).join('\n');
    const msg = `Olá ${pedido.cliente_nome}! Seu pedido:\n${itensTexto}\nTotal: ${formatarMoeda(pedido.valor_total)}\nEntrega: ${formatarData(pedido.data_entrega)} ${pedido.horario_entrega || ''}`;
    window.open(`https://wa.me/${num}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  const addPagamento = () => onAddPagamento && onAddPagamento(pedido);

  const removePagamento = async (pagId) => {
    if (!await confirm('Remover pagamento?', { danger: true, confirmText: 'Excluir' })) return;
    try { await api.delete(`/api/pedidos/pagamentos/${pagId}`); onRefresh(); } catch {}
  };

  const isEntrega = !!pedido.endereco_entrega;
  const labelTipo = isEntrega ? 'Entrega' : 'Retirada';
  const Field = ({ label, children }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
      <span style={{ fontSize: 11, color: '#999', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</span>
      <span style={{ fontSize: 13, color: theme.colors.text }}>{children}</span>
    </div>
  );

  return (
    <div style={cs.overlay} {...overlayCloseProps(onClose)} className="modal-overlay-mobile">
      <div style={{ ...cs.modal, maxWidth: 520, padding: 0, overflow: 'hidden' }} className="modal-content-sheet">
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #f0f0f0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Pedido #{pedido.id}</h3>
            <span style={{ background: `${STATUS_CORES[pedido.status]}20`, color: STATUS_CORES[pedido.status], padding: '3px 10px', borderRadius: 12, fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              {STATUS_LABELS[pedido.status]}
            </span>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#bbb' }}><FaTimes /></button>
        </div>

        <div style={{ padding: '16px 20px', maxHeight: 'calc(92vh - 120px)', overflowY: 'auto' }}>
          {/* Ações rápidas */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
            {pedido.cliente_telefone && <button onClick={abrirWhatsApp} style={{ ...cs.btnSmall, background: '#25d366', color: '#fff' }}><FaWhatsapp /> WhatsApp</button>}
            {/* Ver Orcamentos.js: no app os dois caem na mesma folha. */}
            {isNative() ? (
              <button onClick={() => imprimirPedido(pedido, 'a4')} title="Compartilhar"
                style={{ ...cs.btnSmall, background: '#eee', color: '#555' }}><FaShareAlt /> Compartilhar</button>
            ) : (
              <>
                <button onClick={() => imprimirPedido(pedido, 'a4')} style={{ ...cs.btnSmall, background: '#eee', color: '#555' }}><FaPrint /> PDF</button>
                <button onClick={() => imprimirPedido(pedido, 'termica')} style={{ ...cs.btnSmall, background: '#eee', color: '#555' }}><FaPrint /> Térmica</button>
              </>
            )}
          </div>

          {/* Cliente + Entrega/Retirada */}
          <div className="form-grid-2" style={{ background: '#faf7f5', borderRadius: 12, padding: 14, marginBottom: 14 }}>
            <Field label="Cliente">
              <div style={{ fontWeight: 600 }}>{pedido.cliente_nome}</div>
              {pedido.cliente_telefone && (
                <div style={{ fontSize: 12, color: '#888', marginTop: 2, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <FaPhone style={{ fontSize: 10 }} /> {formatarTelefone(pedido.cliente_telefone)}
                </div>
              )}
            </Field>
            <Field label={labelTipo}>
              {pedido.data_entrega ? (
                <>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <FaCalendarAlt style={{ fontSize: 10, color: '#999' }} /> {formatarData(pedido.data_entrega)}
                    {pedido.horario_entrega && <> <FaClock style={{ fontSize: 10, color: '#999', marginLeft: 4 }} /> {pedido.horario_entrega.slice(0,5)}</>}
                  </div>
                  {pedido.endereco_entrega && (
                    <div style={{ fontSize: 12, color: '#888', marginTop: 2, display: 'flex', alignItems: 'flex-start', gap: 4 }}>
                      <FaMapMarkerAlt style={{ fontSize: 10, marginTop: 3 }} /> <span>{pedido.endereco_entrega}</span>
                    </div>
                  )}
                </>
              ) : <span style={{ color: '#bbb' }}>Sem data</span>}
            </Field>
          </div>

          {/* Itens */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, color: '#999', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>Itens</div>
            {pedido.itens?.map(i => (
              <div key={i.id} style={{ padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <span style={{ fontWeight: 500 }}>{i.quantidade > 1 && `${i.quantidade}× `}{i.tipo === 'bolo' ? i.tamanho_nome : i.descricao_manual}</span>
                  <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{formatarMoeda((i.preco_unitario||0) * (i.quantidade||1))}</span>
                </div>
                {i.tipo === 'bolo' && (i.massa_ficha_nome || i.recheio_principal_nome || i.cobertura || i.decoracao) && (
                  <div style={{ fontSize: 12, color: '#888', marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {i.massa_ficha_nome && <span>🍰 {i.massa_ficha_nome}</span>}
                    {i.recheio_principal_nome && <span>🧁 {i.recheio_principal_nome}</span>}
                    {i.cobertura && <span>🎂 {i.cobertura}</span>}
                  </div>
                )}
                {i.decoracao && <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>🎨 {i.decoracao}</div>}
                {i.imagem_inspiracao && (
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                    {i.imagem_inspiracao.split(',').map(s => s.trim()).filter(Boolean).map((url, j) => (
                      <img key={j} src={url} alt={`Ref ${j + 1}`} style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 6, border: `1px solid ${theme.colors.border}` }} />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Totais */}
          <div style={{ background: '#faf7f5', borderRadius: 12, padding: '10px 14px', marginBottom: 14 }}>
            {(pedido.taxa_entrega || 0) > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#888', padding: '2px 0' }}>
                <span>Taxa de entrega</span><span>{formatarMoeda(pedido.taxa_entrega)}</span>
              </div>
            )}
            {(pedido.desconto || 0) > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#888', padding: '2px 0' }}>
                <span>Desconto</span><span>−{formatarMoeda(pedido.desconto)}</span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontWeight: 700, fontSize: 15, color: theme.colors.primary }}>
              <span>Total</span><span>{formatarMoeda(pedido.valor_total)}</span>
            </div>
            {(pedido.valor_pago || 0) > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#27ae60', padding: '2px 0' }}>
                <span>Pago</span><span>{formatarMoeda(pedido.valor_pago)}</span>
              </div>
            )}
            {(pedido.valor_restante || 0) > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#c0392b', padding: '2px 0', fontWeight: 600 }}>
                <span>Falta</span><span>{formatarMoeda(pedido.valor_restante)}</span>
              </div>
            )}
          </div>

          {/* Pagamentos compactos */}
          {(pedido.pagamentos?.length > 0 || !pedido.pago_completo) && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, color: '#999', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>Pagamentos</div>
              {pedido.pagamentos?.map(p => {
                const corBg = p.tipo === 'sinal' ? '#fff9e6' : '#eafaf1';
                const corTxt = p.tipo === 'sinal' ? '#d4a017' : '#27ae60';
                const tipoLabel = p.tipo === 'sinal' ? 'Sinal' : 'Pagamento';
                return (
                  <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, background: corBg, borderRadius: 8, padding: '6px 10px', marginBottom: 4 }}>
                    <span style={{ fontWeight: 700, color: corTxt, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', minWidth: 64 }}>{tipoLabel}</span>
                    <span style={{ fontWeight: 600, fontSize: 13 }}>{formatarMoeda(p.valor)}</span>
                    <span style={{ fontSize: 12, color: '#888' }}>· {METODO_LABEL[p.metodo] || p.metodo}</span>
                    {p.data && <span style={{ fontSize: 11, color: '#aaa' }}>· {formatarData(p.data)}</span>}
                    <button onClick={() => removePagamento(p.id)} title="Remover"
                      style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: '#c0392b', fontSize: 11, padding: 0 }}>
                      <FaTimes />
                    </button>
                  </div>
                );
              })}
              {!pedido.pago_completo && (
                <button onClick={addPagamento} style={{ ...cs.btnSmall, background: '#27ae60', color: '#fff', width: '100%', justifyContent: 'center', marginTop: 4 }}>
                  <FaPlus style={{ fontSize: 10 }} /> Registrar pagamento ({formatarMoeda(pedido.valor_restante)})
                </button>
              )}
            </div>
          )}

          {/* Notas */}
          {pedido.notas && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, color: '#999', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>Notas</div>
              <div style={{ fontSize: 13, color: theme.colors.text, fontStyle: 'italic' }}>{pedido.notas}</div>
            </div>
          )}

          <div style={{ fontSize: 11, color: '#ccc', textAlign: 'center', marginTop: 4 }}>
            Criado em {pedido.criado_em ? new Date(pedido.criado_em).toLocaleString('pt-BR') : ''}
          </div>
        </div>

        {/* Footer fixo */}
        {onCancelar && pedido.status !== 'cancelado' && pedido.status !== 'entregue' && (
          <div style={{ padding: '12px 20px', borderTop: '1px solid #f0f0f0', background: '#fafafa' }}>
            <button onClick={() => onCancelar(pedido)}
              style={{ width: '100%', padding: '9px 0', borderRadius: 8, border: '1px solid #e74c3c', background: '#fff', color: '#e74c3c', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
              Cancelar pedido
            </button>
          </div>
        )}
      </div>
    </div>
  );
};


// ===========================================================================
// Print
// ===========================================================================
function imprimirPedido(pedido, formato) {
  const fmt = (v) => `R$ ${(v||0).toFixed(2).replace('.',',')}`;
  const fmtData = (d) => d ? new Date(d+'T00:00:00').toLocaleDateString('pt-BR') : '';
  const esc = (s) => String(s ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  const largura = formato === 'termica' ? '80mm' : '210mm';

  const itensHtml = (pedido.itens || []).map(i => {
    const nome = i.tipo === 'bolo' ? (i.tamanho_nome || 'Bolo') : (i.descricao_manual || 'Item');
    const detalhe = i.tipo === 'bolo' ? [i.massa_ficha_nome, i.recheio_principal_nome, i.cobertura, i.decoracao].filter(Boolean).join(' | ') : '';
    const qtd = Number(i.quantidade) || 1;
    return `<tr><td style="padding:4px 0">${esc(nome)} x${qtd}${detalhe ? `<br><small style="color:#888">${esc(detalhe)}</small>` : ''}</td><td style="text-align:right;padding:4px 0">${fmt((i.preco_unitario||0)*qtd)}</td></tr>`;
  }).join('');

  const pagamentosHtml = (pedido.pagamentos || []).map(p =>
    `<div style="margin:4px 0;padding:6px;background:${p.tipo==='sinal'?'#fff9e6':'#eafaf1'};border-radius:6px"><strong>${p.tipo==='sinal'?'Sinal':'Pagamento'}:</strong> ${fmt(p.valor)} via ${esc(METODO_LABEL[p.metodo]||p.metodo)} ${p.data ? '- '+esc(fmtData(p.data)) : ''}</div>`
  ).join('');

  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><style>
    body { font-family: 'Segoe UI', sans-serif; margin: 0; padding: 16px; width: ${largura}; font-size: ${formato==='termica'?'11px':'14px'}; }
    h2 { margin: 0 0 8px; font-size: ${formato==='termica'?'14px':'20px'}; }
    table { width: 100%; border-collapse: collapse; }
    .divider { border-top: 1px dashed #ccc; margin: 8px 0; }
    .total { font-weight: 700; font-size: ${formato==='termica'?'13px':'16px'}; color: #d4807c; }
  </style></head><body>
    <h2>${esc(pedido.cliente_nome)}</h2>
    <div style="color:#888;margin-bottom:8px">${esc(fmtData(pedido.data_entrega))} ${esc(pedido.horario_entrega||'')}</div>
    ${pedido.endereco_entrega ? `<div style="margin-bottom:8px">📍 ${esc(pedido.endereco_entrega)}</div>` : ''}
    ${pedido.cliente_telefone ? `<div style="margin-bottom:8px">📞 ${esc(formatarTelefone(pedido.cliente_telefone))}</div>` : ''}
    <div class="divider"></div>
    <table>${itensHtml}</table>
    ${(pedido.taxa_entrega||0) > 0 ? `<div style="text-align:right;color:#888;margin-top:4px">Taxa de Entrega: ${fmt(pedido.taxa_entrega)}</div>` : ''}
    ${(pedido.desconto||0) > 0 ? `<div style="text-align:right;color:#888">Desconto: -${fmt(pedido.desconto)}</div>` : ''}
    <div class="divider"></div>
    <div style="text-align:right" class="total">Total: ${fmt(pedido.valor_total)}</div>
    <div class="divider"></div>
    ${pagamentosHtml}
    ${pedido.valor_restante > 0 ? `<div style="margin-top:8px;color:#e74c3c;font-weight:600">Falta: ${fmt(pedido.valor_restante)}</div>` : '<div style="margin-top:8px;color:#27ae60;font-weight:600">Pago integralmente</div>'}
    ${pedido.notas ? `<div class="divider"></div><div style="color:#888"><strong>Notas:</strong> ${esc(pedido.notas)}</div>` : ''}
  </body></html>`;

  // Mesmo conteudo do HTML acima, em blocos — e o que o app desenha no PDF.
  // Mexeu num, olhe o outro. O porque de existirem os dois esta em
  // platform/arquivos.js.
  const blocos = [
    { t: 'titulo', texto: pedido.cliente_nome },
    { t: 'linha', texto: `${fmtData(pedido.data_entrega)} ${pedido.horario_entrega || ''}`.trim(), fraco: true },
    pedido.endereco_entrega && { t: 'linha', texto: `Entrega: ${pedido.endereco_entrega}` },
    pedido.cliente_telefone && { t: 'linha', texto: `Telefone: ${formatarTelefone(pedido.cliente_telefone)}` },
    { t: 'divisoria' },
    ...(pedido.itens || []).map(i => {
      const nome = i.tipo === 'bolo' ? (i.tamanho_nome || 'Bolo') : (i.descricao_manual || 'Item');
      const detalhe = i.tipo === 'bolo'
        ? [i.massa_ficha_nome, i.recheio_principal_nome, i.cobertura, i.decoracao].filter(Boolean).join(' | ')
        : '';
      const qtd = Number(i.quantidade) || 1;
      return { t: 'item', nome: `${nome} x${qtd}`, detalhe, valor: fmt((i.preco_unitario || 0) * qtd) };
    }),
    (pedido.taxa_entrega || 0) > 0 && { t: 'par', esq: 'Taxa de entrega', dir: fmt(pedido.taxa_entrega), fraco: true },
    (pedido.desconto || 0) > 0 && { t: 'par', esq: 'Desconto', dir: `-${fmt(pedido.desconto)}`, fraco: true },
    { t: 'divisoria' },
    { t: 'par', esq: 'Total', dir: fmt(pedido.valor_total), forte: true },
    ...((pedido.pagamentos || []).length ? [{ t: 'divisoria' }] : []),
    ...(pedido.pagamentos || []).map(p => ({
      t: 'par',
      esq: `${p.tipo === 'sinal' ? 'Sinal' : 'Pagamento'} - ${METODO_LABEL[p.metodo] || p.metodo}${p.data ? ' - ' + fmtData(p.data) : ''}`,
      dir: fmt(p.valor),
    })),
    { t: 'linha', texto: pedido.valor_restante > 0 ? `Falta: ${fmt(pedido.valor_restante)}` : 'Pago integralmente', forte: true },
    pedido.notas && { t: 'divisoria' },
    pedido.notas && { t: 'linha', texto: `Notas: ${pedido.notas}`, fraco: true },
  ];

  return imprimirRecibo({
    html,
    blocos,
    nome: `pedido-${pedido.id}.pdf`,
    larguraMm: formato === 'termica' ? 80 : 210,
  });
}


// ===========================================================================
// Styles
// ===========================================================================
const cs = {
  input: t.input,
  inputModal: t.input,
  label: { ...t.label, fontWeight: 700 },
  btnPrimary: t.btnPrimary,
  btnSmall: t.btnSmall,
  iconBtn: t.btnIcon,
  viewBtn: { padding: '8px 16px', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 500, background: '#fff', color: theme.colors.textMuted, display: 'flex', alignItems: 'center', gap: 6, transition: 'all 0.15s' },
  viewActive: { background: theme.colors.bgSection, color: theme.colors.primary },
  toggleBtn: { padding: '10px 0', borderRadius: 10, border: `1.5px solid ${theme.colors.border}`, background: '#fff', color: theme.colors.textSecondary, fontSize: 14, fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s' },
  toggleActive: { background: theme.colors.primary, color: '#fff', borderColor: theme.colors.primary },
  statusBadge: { padding: '4px 12px', borderRadius: 20, fontSize: 13, fontWeight: 600, border: '1px solid', cursor: 'pointer', appearance: 'none' },
  card: { ...t.card, padding: '16px 20px' },
  overlay: t.overlay,
  modal: t.modal,
  error: t.error,
  calContainer: { ...t.card, padding: 24, marginBottom: 24 },
  calHead: { padding: '10px 0', textAlign: 'center', fontSize: 13, fontWeight: 600, color: theme.colors.textMuted },
  calCell: { padding: '12px 4px', textAlign: 'center', verticalAlign: 'top', height: 70, borderTop: `1px solid ${theme.colors.borderLight}` },
  calDay: { fontSize: 14, fontWeight: 500 },
  calBadge: { position: 'absolute', top: -6, right: -6, background: theme.colors.primary, color: '#fff', borderRadius: '50%', width: 18, height: 18, fontSize: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 },
  calNav: { background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: theme.colors.textMuted, padding: 8 },
};

export default Pedidos;
